
import React, { useState } from 'react'; // Removed unused imports
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import Header from './components/Header';
import Footer from './components/Footer';
import LandingPage from './components/LandingPage';
import PrivacyPolicy from './components/PrivacyPolicy';
import Opus3DPrivacyPolicy from './components/Opus3DPrivacyPolicy';
import Opus3DDeleteAccount from './components/Opus3DDeleteAccount';
import Opus3DTerms from './components/Opus3DTerms';
import TermsOfService from './components/TermsOfService';
import WelcomeCurtain from './components/WelcomeCurtain';
import InquiryModal from './components/InquiryModal';
import ExitIntent from './components/ExitIntent';
import { useAuth } from './contexts/AuthContext';

const VerificationBanner: React.FC = () => {
  const { currentUser, resendVerification } = useAuth();
  const [resending, setResending] = useState(false);
  const [sent, setSent] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  if (!currentUser || currentUser.emailVerified) return null;

  const handleResend = async () => {
    setResending(true);
    setErrorMsg('');
    try {
      await resendVerification();
      setSent(true);
      setTimeout(() => setSent(false), 5000);
    } catch (err: any) {
      console.error(err);
      setErrorMsg('Failed to send. Wait a moment and try again.');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="bg-[#FF5C00] text-white py-2 px-6 text-[10px] md:text-xs font-black uppercase tracking-widest flex flex-wrap items-center justify-center gap-4 animate-reveal z-[60] relative">
      <div className="flex items-center gap-2">
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" /></svg>
        <span>Please verify your email to access all features.</span>
      </div>
      <div className="flex items-center gap-2">
        <button
          onClick={handleResend}
          disabled={resending || sent}
          className="bg-white/10 hover:bg-white/20 px-3 py-1 rounded-full border border-white/20 transition-all disabled:opacity-50"
        >
          {sent ? 'Sent!' : resending ? 'Sending...' : 'Resend Link'}
        </button>
        {errorMsg && <span className="text-[9px] bg-red-600 px-2 py-0.5 rounded text-white">{errorMsg}</span>}
      </div>
    </div>
  );
};

const StickyMobileAction: React.FC<{ onBookDemo: () => void }> = ({ onBookDemo }) => {
  const [isVisible, setIsVisible] = useState(false);
  // React.useEffect is needed here if not imported at top, but let's assume imports are handled.
  // Actually let's just use React.useEffect to be safe or ensure useEffect is imported.
  React.useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 600) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div
      className={`fixed bottom-0 left-0 right-0 z-[100] p-4 lg:hidden transition-all duration-500 transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'
        }`}
    >
      <div className="glass shadow-2xl rounded-2xl p-3 flex items-center gap-3 border border-white/40">
        <div className="flex items-center gap-2">
          <a href="https://wa.me/919902594100" target="_blank" rel="noopener noreferrer" className="w-12 h-12 flex items-center justify-center bg-[#25D366] text-white rounded-xl shadow-lg active:scale-90" aria-label="WhatsApp">
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.246 2.248 3.484 5.232 3.484 8.412-.003 6.557-5.338 11.892-11.893 11.892-1.996-.001-3.951-.5-5.688-1.448l-6.309 1.656zm6.222-4.032c1.503.893 3.129 1.364 4.789 1.365h.004c5.42 0 9.832-4.412 9.835-9.835.001-2.628-1.023-5.1-2.885-6.963-1.862-1.863-4.334-2.888-6.962-2.889-5.424 0-9.835 4.412-9.838 9.835-.001 1.811.497 3.54 1.441 5.01l-.924 3.371 3.45-.904zm10.798-7.345c-.07-.117-.258-.187-.539-.327-.281-.14-.563-.081-.772.23l-.149.223c-.164.247-.334.501-.547.433-.213-.068-.899-.332-1.713-1.058-.633-.565-1.06-1.263-1.184-1.477-.124-.213-.013-.329.093-.433.096-.094.213-.247.32-.37.106-.124.141-.213.213-.353.071-.141.035-.265-.018-.37-.053-.106-.477-1.147-.654-1.571-.172-.416-.347-.359-.477-.366l-.407-.006c-.141 0-.37.053-.564.265-.194.212-.741.724-.741 1.765s.759 2.048.865 2.19c.106.141 1.494 2.282 3.619 3.197.505.217.9.346 1.208.444.509.162.973.139 1.339.085.408-.06 1.25-.511 1.427-.999.177-.488.177-.905.124-.993-.053-.088-.194-.141-.475-.281z" /></svg>
          </a>
          <a href="tel:+919902594100" className="w-12 h-12 flex items-center justify-center bg-[#003B5C] text-white rounded-xl shadow-lg active:scale-90" aria-label="Call">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
          </a>
        </div>
        <button
          onClick={onBookDemo}
          className="flex-1 bg-[#FF5C00] text-white font-black text-xs uppercase tracking-widest py-4 rounded-xl text-center shadow-lg active:scale-[0.98] transition-all"
        >
          Book Demo
        </button>
      </div>
      <div className="h-[env(safe-area-inset-bottom)]"></div>
    </div>
  );
};

const AppContent: React.FC = () => {
  const [isInquiryModalOpen, setIsInquiryModalOpen] = useState(false);
  const location = useLocation();

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <div className="bg-white text-gray-800 min-h-screen selection:bg-[#FF5C00]/20 selection:text-[#FF5C00]">
      <VerificationBanner />
      <WelcomeCurtain />
      <Header />
      <main>
        <Routes>
          <Route path="/" element={<LandingPage onBookDemo={() => setIsInquiryModalOpen(true)} />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/opus3d-privacy" element={<Opus3DPrivacyPolicy />} />
          <Route path="/opus3d-delete-account" element={<Opus3DDeleteAccount />} />
          <Route path="/opus3d-terms" element={<Opus3DTerms />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      <Footer />
      <StickyMobileAction onBookDemo={() => setIsInquiryModalOpen(true)} />
      <InquiryModal
        isOpen={isInquiryModalOpen}
        onClose={() => setIsInquiryModalOpen(false)}
      />
      {!['/opus3d-privacy', '/opus3d-delete-account', '/opus3d-terms'].includes(location.pathname) && <ExitIntent />}
    </div>
  );
};

import { HelmetProvider } from 'react-helmet-async';

const App: React.FC = () => {
  return (
    <HelmetProvider>
      <Router>
        <AppContent />
      </Router>
    </HelmetProvider>
  );
};

export default App;
